import * as React from 'react';
import { CheckLine } from '@mingcute/react';

import { cn } from '../lib/cn';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

// EU VAT format patterns (simplified — real validation should hit VIES server-side).
const VAT_PATTERNS: Record<string, RegExp> = {
  CZ: /^CZ\d{8,10}$/,
  SK: /^SK\d{10}$/,
  AT: /^ATU\d{8}$/,
  DE: /^DE\d{9}$/,
  PL: /^PL\d{10}$/,
  HU: /^HU\d{8}$/,
  FR: /^FR[A-HJ-NP-Z0-9]{2}\d{9}$/,
  IT: /^IT\d{11}$/,
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
  NL: /^NL\d{9}B\d{2}$/,
  GB: /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
};

export const VAT_COUNTRIES = Object.keys(VAT_PATTERNS);

export interface VatValue {
  country: string;
  number: string; // raw national portion, no prefix
}

export interface VatInputProps {
  value?: VatValue;
  onChange?: (v: VatValue) => void;
  countries?: string[];
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  externallyVerified?: boolean; // server VIES check passed
  onValidityChange?: (valid: boolean) => void;
}

export function validateVat(country: string, full: string): boolean {
  const pat = VAT_PATTERNS[country];
  if (!pat) return false;
  return pat.test(full.toUpperCase());
}

export const VatInput = React.forwardRef<HTMLInputElement, VatInputProps>(
  (
    {
      value = { country: 'CZ', number: '' },
      onChange,
      countries = VAT_COUNTRIES,
      invalid,
      disabled,
      className,
      id,
      externallyVerified,
      onValidityChange,
    },
    ref,
  ) => {
    const full = `${value.country}${value.number.replace(/\s/g, '')}`;
    const formatValid = value.number.length > 0 && validateVat(value.country, full);
    const showCheck = externallyVerified ?? formatValid;

    React.useEffect(() => {
      onValidityChange?.(formatValid);
    }, [formatValid, onValidityChange]);

    return (
      <div
        className={cn(
          'flex h-9 items-center gap-1 rounded-[var(--radius-md)] border bg-[var(--color-surface)] pl-1 pr-3',
          'focus-within:ring-2 focus-within:ring-[var(--color-ring)]',
          invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        <Select
          value={value.country}
          onValueChange={(c) => onChange?.({ ...value, country: c })}
          disabled={disabled}
        >
          <SelectTrigger className="h-7 w-auto gap-1 border-0 bg-transparent px-2 text-sm focus-visible:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          ref={ref}
          id={id}
          type="text"
          disabled={disabled}
          value={value.number}
          onChange={(e) => onChange?.({ ...value, number: e.target.value.replace(/\s/g, '').toUpperCase() })}
          placeholder="12345678"
          aria-invalid={invalid || undefined}
          className="h-full flex-1 bg-transparent text-sm text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-muted)]"
        />
        {showCheck ? (
          <CheckLine size={16} className="shrink-0 text-[var(--color-success)]" aria-label="Valid VAT format" />
        ) : null}
      </div>
    );
  },
);
VatInput.displayName = 'VatInput';
